/**
 * Card-label brand dedupe (DNKADM-0355). pay-api vaults some cards with the brand
 * ALREADY embedded in `maskedPan` (`"VISA **** 1111"` rather than `"**** 1111"`), so
 * anything that names the card by brand + masked pan can end up rendering the brand
 * twice — the staging report was **"VISA VISA **** 1111"** on member detail. dink-api's
 * `maskedCard` is composed upstream from that same value and arrives doubled on its own.
 *
 * This is the render-boundary guard for that, in the same spirit as `scrubBrand`
 * (`./whitelabel`): a small pure function applied where a backend-sourced card string
 * meets the screen. It is deliberately NOT in `whitelabel.ts` — that module's "brand" is
 * the white-label product name (GoHighLevel → Journix) and this one's is a card network;
 * one file meaning two different things by "brand" is a hazard on a money-adjacent label.
 *
 * **STOPGAP.** The real fix is pay-api emitting an unprefixed `maskedPan` (dink-api
 * backlog `0050`). When that ships this dedupe becomes a no-op and can be retired —
 * there is deliberately no removal machinery, feature flag, or dated switch here.
 *
 * **Scope is intentionally tiny.** This does not detect brands, reformat, or touch a PAN.
 * It collapses ONE thing: a repeat of the leading whitespace-delimited token, and only
 * when that token contains a letter. It reads no digits and rewrites nothing after the
 * leading run, so a card's identifying digits are never touched.
 *
 * The module also carries {@link composeCardLabel} (DNKADM-0365) — the same
 * leading-brand question, asked at composition time for the one surface that joins the
 * two fields with a separator instead of a space. See its own note below.
 */

/**
 * A repeat of the FIRST token, at the start of the string:
 *  - `(\s*)` — any leading whitespace, preserved verbatim.
 *  - `(\S*[A-Za-z]\S*)` — the first token, preserved verbatim including its original
 *    casing, which is why `"Visa VISA …"` keeps `"Visa"`. It must contain a LETTER: the
 *    spec's unit is a repeated leading *word*, and a run of mask glyphs is not a word.
 *    Without that requirement a four-group mask — `"**** **** **** 1111"`, or the bullet
 *    form — reads as a token repeated twice and the guard would eat the mask itself,
 *    rewriting a card's masked display. Unreachable through `cardLabel` (whose composed
 *    string always leads with `cardBrand`) but reachable through `MembershipSummaryBar`,
 *    which hands a whole upstream string straight in.
 *  - `(?:\s+\2(?=\s|$))+` — one or more further copies of that exact token, each a WHOLE
 *    token (the lookahead stops `"VISA VISAX"` matching) and each dropped. Under the `i`
 *    flag the backreference compares case-insensitively, so `"VISA Visa"` collapses too.
 *
 * Anchored at `^` and never global: only the leading run is touched, and a string whose
 * first token does not immediately repeat cannot match at all.
 */
const LEADING_TOKEN_REPEAT = /^(\s*)(\S*[A-Za-z]\S*)(?:\s+\2(?=\s|$))+/i;

/**
 * Collapse a duplicated leading brand token in a card label so the brand renders once:
 * `"VISA VISA **** 1111"` → `"VISA **** 1111"`, `"Visa VISA **** 1111"` → `"Visa **** 1111"`.
 *
 * A label with no such repeat passes through **byte-identical** — including a label that
 * never carried a brand (`"**** 1111"`) and an empty string. `null` passes through as
 * `null`, so this composes directly with `scrubBrand` on the nullable render chain.
 */
export function dedupeCardBrand(label: string): string;
export function dedupeCardBrand(label: string | null): string | null;
export function dedupeCardBrand(label: string | null): string | null {
  if (label === null) return null;
  return label.replace(LEADING_TOKEN_REPEAT, '$1$2');
}

/**
 * DNKADM-0365 — the same question ("does the masked pan already lead with the brand?")
 * asked BEFORE a separator is added rather than after. `UpcomingChargesScreen` names a
 * card as `brand · masked`, so the pay-api shape that doubled the member-detail line
 * ("VISA VISA **** 1111") doubles this one as "VISA · VISA **** 1111" — a repeat the
 * dedupe above cannot see, because the separator sits between the two copies.
 *
 * The fix is the COMPOSITION, not a wider rule: when the masked pan already begins with
 * the brand there is no second copy to add. `dedupeCardBrand` is untouched, and this
 * function reads no digits and rewrites no string — it only chooses between the two it
 * was handed, and returns the chosen one verbatim.
 */

/** Whitespace after the brand is what makes it a whole token rather than a prefix. */
const WHITESPACE = /\s/;

/**
 * Does `masked` begin with `brand` as a WHOLE leading token run — not merely as a
 * character prefix?
 *
 *  - The brand is compared trimmed and case-insensitively, so `'visa'` recognizes
 *    `'VISA **** 1111'`. `masked` is only ever compared, never rewritten.
 *  - A BLANK brand (empty or whitespace-only) never matches. Without that guard the
 *    comparison degenerates — every string "begins with" the empty string — and a label
 *    would silently drop a field on the strength of a vacuous match. A blank brand falls
 *    through to the ordinary composition instead, which is byte-for-byte what today's
 *    `join` already renders for it.
 *  - The character after the brand must be whitespace or the end of the string. THAT is
 *    the whole-token requirement, and it is the whole difference from a prefix match:
 *    `'VISACARD 1111'` starts with the letters of `'VISA'`, but its leading token is a
 *    DIFFERENT token, so the brand still has to be named alongside it.
 *
 * The boundary test also covers a multi-word brand ("AMERICAN EXPRESS"), which a
 * single-token comparison would miss.
 */
function maskedLeadsWithBrand(brand: string, masked: string): boolean {
  const token = brand.trim();
  if (token === '') return false;
  const rest = masked.trimStart();
  if (!rest.toLowerCase().startsWith(token.toLowerCase())) return false;
  const next = rest.charAt(token.length);
  return next === '' || WHITESPACE.test(next);
}

/**
 * Compose a card's ONE display label from its brand and masked pan, joined with
 * `separator` — except when the masked pan already leads with the brand, in which case
 * the masked pan alone IS the label and the brand is not named a second time:
 * `('VISA', 'VISA **** 1111', ' · ')` → `'VISA **** 1111'`, while
 * `('VISA', '**** 1111', ' · ')` → `'VISA · **** 1111'` as before.
 *
 * Either side may be absent: a `null` brand renders the masked pan alone, a `null`
 * masked pan the brand alone, and both `null` the empty string — the caller decides what
 * a card known by neither field reads as. Whichever string is returned comes back
 * VERBATIM; a masked pan's digits are never read, reformatted, or edited.
 */
export function composeCardLabel(
  brand: string | null,
  masked: string | null,
  separator: string,
): string {
  if (brand === null) return masked ?? '';
  if (masked === null) return brand;
  if (maskedLeadsWithBrand(brand, masked)) return masked;
  return `${brand}${separator}${masked}`;
}
